import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export interface BillingPricingConfig {
  creditsPerUsd: number;
  overheadFactor: number;
  targetMargin: number;
  paymentFeeRate: number;
  taxRate: number;
  fxBuffer: number;
  minUtilization: number;
  estimateSafetyFactor: number;
  vndPerUsd: number | null;
  vndPerUsdSource: 'auto' | 'manual' | null;
  vndPerUsdFetchedAt: string | null;
  unitCosts: Array<{ key: string; label: string; avgCostUsd: number; sampleSize: number }>;
}
export type BillingReportGroupBy = 'model' | 'capability' | 'day';
export interface BillingCostReportParams {
  from?: string;
  to?: string;
  groupBy: BillingReportGroupBy;
}
export interface BillingCostReportRow {
  model: string;
  capabilityCode: string;
  day: string;
  calls: number;
  promptTokens: number;
  completionTokens: number;
  creditsCharged: number;
  costUsdCatalog: number | null;
  gatewayCostUsd: number | null;
  varianceUsd: number | null;
  variancePercent: number | null;
  flagged: boolean;
}
export interface BillingCostReport {
  rows: BillingCostReportRow[];
  totals: {
    calls: number;
    costUsdCatalog: number;
    gatewayCostUsd: number;
    overheadRatio: number | null;
    rowsWithoutCatalogCost: number;
    usersNullCalls: number;
  };
}
export const useBillingPricingConfig = () =>
  useQuery({
    queryKey: ['billing', 'pricing-config'],
    queryFn: async () =>
      (await axiosInstance.get<BillingPricingConfig>('/v1/admin/billing/pricing-config')).data,
    staleTime: 60_000,
  });
export const useBillingCostReport = (params: BillingCostReportParams) =>
  useQuery({
    queryKey: ['billing', 'cost-report', params],
    queryFn: async () =>
      (await axiosInstance.get<BillingCostReport>('/v1/admin/billing/cost-report', { params }))
        .data,
    staleTime: 60_000,
  });

export interface PackEstimateUnit {
  code: string;
  label: { en: string; vi: string };
  anchorSource: string;
  costUsd?: number | null;
}
export interface PackEstimateUnitConfig extends PackEstimateUnit {
  measured: { sampleSize: number; avgCostUsd: number | null };
  effectiveCostUsd: number | null;
}
export interface PackEstimateUnitsResponse {
  creditsPerUsd: number;
  units: PackEstimateUnitConfig[];
}
const packEstimateUnitsKey = ['billing', 'pack-estimate-units'];
export const usePackEstimateUnits = () =>
  useQuery({
    queryKey: packEstimateUnitsKey,
    queryFn: async () =>
      (await axiosInstance.get<PackEstimateUnitsResponse>('/v1/admin/billing/pack-estimate-units'))
        .data,
  });
export const useSavePackEstimateUnits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (units: PackEstimateUnit[]) =>
      (
        await axiosInstance.put<PackEstimateUnitsResponse>(
          '/v1/admin/billing/pack-estimate-units',
          { units }
        )
      ).data,
    onSuccess: (data) => queryClient.setQueryData(packEstimateUnitsKey, data),
  });
};
